import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavigationMode, NavigationContextType } from '../types/navigation';

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [currentMode, setCurrentMode] = useState<NavigationMode>('dashboard');
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      // Use 992px breakpoint to match Mantine's 'md' breakpoint (consistent with AppShell navbar)
      setIsMobile(window.innerWidth < 992);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine current mode from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/notes')) {
      setCurrentMode('notes');
    } else if (path.startsWith('/calendar')) {
      setCurrentMode('calendar');
    } else if (path.startsWith('/kanban')) {
      setCurrentMode('kanban');
    } else if (path.startsWith('/todos')) {
      setCurrentMode('todos');
    } else if (path === '/' || path.startsWith('/dashboard')) {
      setCurrentMode('dashboard');
    } else {
      setCurrentMode('dashboard'); // Default to dashboard
    }
  }, [location.pathname]);

  // Close navbar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setIsNavbarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleSetCurrentMode = (mode: NavigationMode) => {
    setCurrentMode(mode);
    
    // Navigate to the appropriate route
    switch (mode) {
      case 'dashboard':
        navigate('/');
        break;
      case 'notes':
        navigate('/notes');
        break;
      case 'calendar':
        navigate('/calendar');
        break;
      case 'kanban':
        navigate('/kanban');
        break;
      case 'todos':
        navigate('/todos');
        break;
      default:
        navigate('/');
        break;
    }

    // Close navbar on mobile after navigation
    if (isMobile) {
      setIsNavbarOpen(false);
    }
  };

  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  const closeNavbar = () => {
    setIsNavbarOpen(false);
  };

  const openNavbar = () => {
    setIsNavbarOpen(true);
  };

  const value: NavigationContextType = {
    currentMode,
    isNavbarOpen,
    isMobile,
    setCurrentMode: handleSetCurrentMode,
    toggleNavbar,
    closeNavbar,
    openNavbar,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
